/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { last } from "lodash";
import { TableProps } from "../../../components/Table";
import { ColDef } from "ag-grid-community";
import { SLIDE_NUMBER } from "../constants";

const COLUMNS_AXIS = "countries.default";
const ROWS_AXIS = "calendar.default";
const CALENDAR_STEP = 10000;

export function useAgGridTransformer(olapData: OLAPTransformerResult | null, highlight: Record<string, number> | null): TableProps | undefined {
  const [state, setState] = useState<TableProps | undefined>();

  const [countries] = useParameter<number[]>('@countries');
  const [calendarYear] = useParameter<number>('@calendar');
  const [commodity] = useParameter<number>('@commodities');
  const [businessUnit] = useParameter<number>('@business-units');
  const calendar = useMemo(() => [-5, -4, -3, -2, -1, 0].map(x => (calendarYear || 0) + (CALENDAR_STEP * x)), [calendarYear]);

  const [countriesDisplayItems] = useDictionary(dictionaryService, "slides-countries-display", undefined, {
    slide: SLIDE_NUMBER,
    olap_commodity: commodity,
    business_unit: businessUnit,
  });
  const categories = useMemo(
    () => uniq(countries?.map<ColDef>(countryId => {
      const column = olapData?.columns.find(col => col.selection[COLUMNS_AXIS] === countryId);
      const colHeader = column?.headers?.find(header => header.id === `${COLUMNS_AXIS}.name`);
      let localeName = colHeader?.name as any;
      if (localeName === undefined) {
        const displayItem = countriesDisplayItems?.find(item => item.getValue<number>("olap_country") === countryId);
        localeName = displayItem?.getValue<any>('name');
      }
      if (typeof localeName === "object") {
        localeName = localeName[9];
      }
      const name = localeName || '';
      return { headerName: name, field: countryId.toString(), resizable: true };
    }) || [] as ColDef[]),
    [olapData, countries]
  );

  useEffect(() => {
    if (olapData) {
      const rows = calendar?.map(calendarYear => {
        const dataRow = olapData?.rows.find(row => Number(row.selection[ROWS_AXIS]) === calendarYear);
        let rowName = dataRow?.attributes[`${ROWS_AXIS}.name`] || '';
        if (!Boolean(rowName)) {
          rowName = calendarYear.toString().slice(1, 5);
        }
        const recordsRow = {
          Period: rowName,
          id: calendarYear,
        };

        countries?.forEach(countryId => {
          const cell = dataRow?.points.find(point => (point.column.selection[COLUMNS_AXIS]) === countryId);
          const isEmpty = cell?.value === undefined || cell?.value === null;
          recordsRow[countryId.toString()] = isEmpty ? '' : cell!.value.toFixed(2);
        });

        return recordsRow;
      });

      const periodCol: ColDef = { headerName: "Period", field: "Period", resizable: true };
      const result: TableProps = {
        columns: [periodCol].concat(categories),
        rows,
      }

      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, calendar, categories, countries]);

  useEffect(() => {
    if (state) {
      setState({
        ...state!,
        getCellClass: highlight === null ? undefined : params => {
          return highlight[ROWS_AXIS] === params.data['id'] && highlight[COLUMNS_AXIS] === Number(params.colDef.field) ? 'cell_highlight' : '';
        },
        getRowClass: highlight === null ? undefined : params => {
          return highlight[ROWS_AXIS] === params.data['id'] ? 'row_highlight' : '';
        }
      });
    }
  }, [highlight]);


  return state;
}
